import React, { useCallback, useEffect, useRef, useState } from "react";
import { Convert, User } from "@/models/responses";
import { ColDef } from "ag-grid-community";
import Grid from "@/lib/organisms/Grid";
import { CustomCellRendererProps, CustomTooltipProps } from "ag-grid-react";
import AddUserDialog from "@/lib/shadcn/components/add-user-dialog";
import { decodeErrorResponse } from "@/utils/errors";
import { errorPopup, successPopup } from "@/utils/alerts";
import Swal from "sweetalert2";
import { dateFormatter } from "@/utils/formatters";
import { booleanSetFilter, utcDateFilterParams } from "@/utils/grid";
import { useAppData } from "@/hooks/useAppData";
import { Pencil, PlusIcon, Trash2 } from "lucide-react";
import { Badge } from "@/lib/shadcn/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/lib/shadcn/ui/tooltip";
import { Button } from "@/lib/shadcn/ui/button";
import { cn } from "@/lib/shadcn/utils";
import { visibleOwnersTooltipComponent } from "@/pages/visibleOwnersTooltipComponent";
import { fetchOptions } from "@/hooks/fetchOptions";
import { apiUrl } from "@/layouts/apiUrl";
import { toast } from "@/lib/shadcn/hooks/use-toast";

export const VisibleOwnersRenderer: React.FC<CustomCellRendererProps> = (params) => {
    const { ownerOptions } = useAppData();
    const containerRef = useRef<HTMLDivElement>(null);

    const owners = ownerOptions.filter((owner) => params.data.visibleOwners.includes(Number(owner.value))).map((owner) => owner.label);

    if (params.data.isAdmin) {
        return (
            <div className="flex items-center h-[40px] p-2">
                <Badge variant="secondary" className="bg-gray-100 text-gray-700">
                    All Owners
                </Badge>
            </div>
        );
    }

    if (owners.length === 0) return null;

    return (
        <div ref={containerRef} className="flex items-center h-[40px] p-2 gap-1.5">
            <Badge variant="secondary" className="px-2 py-1 whitespace-nowrap">
                {owners[0]}
            </Badge>

            {owners.length > 1 && (
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Badge variant="outline" className="px-2 py-1 cursor-help whitespace-nowrap">
                            +{owners.length - 1} more
                        </Badge>
                    </TooltipTrigger>
                    <TooltipContent>
                        <p>Owners</p>
                    </TooltipContent>
                </Tooltip>
            )}
        </div>
    );
};

interface ExtendedActionsRendererProps extends CustomCellRendererProps {
    onEdit: (id: number) => void;
    onDelete: (id: number) => void;
}

export const ActionsRenderer: React.FC<ExtendedActionsRendererProps> = (params) => {
    return (
        <div className="flex items-center gap-2 p-2">
            <Tooltip>
                <TooltipTrigger asChild>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 p-0 hover:bg-gray-100"
                        onClick={() => params.onEdit(params.data.id)}
                    >
                        <Pencil className="h-4 w-4" />
                    </Button>
                </TooltipTrigger>
                <TooltipContent>Edit user</TooltipContent>
            </Tooltip>

            <Tooltip>
                <TooltipTrigger asChild>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 p-0 hover:bg-red-100 hover:text-red-600"
                        onClick={() => params.onDelete(params.data.id)}
                    >
                        <Trash2 className="h-4 w-4" />
                    </Button>
                </TooltipTrigger>
                <TooltipContent>Delete user</TooltipContent>
            </Tooltip>
        </div>
    );
};

const UsersPage: React.FC = () => {
    const [users, setUsers] = useState<User[]>([]);
    const [userToEdit, setUserToEdit] = useState<User | undefined>(undefined);
    const [showAddUserModal, setShowAddOrEditUserModal] = useState(false);
    const gridName = "Users";
    const { ownerIdMap } = useAppData();
    const deleteUser = useCallback(
        (userId: number) => {
            const user: User = users.find((user) => user.id === userId)!;
            void Swal.fire({
                title: `Are you sure you want to delete user ${user.givenName} ${user.lastName}?`,
                text: "You won't be able to revert this!",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#0f212c",
                cancelButtonColor: "#f0bc74",
                confirmButtonText: "Yes, delete!",
            }).then((result) => {
                if (result.isConfirmed) {
                    fetch(`${apiUrl}/api/admin/users/${userId}`, {
                        method: "DELETE",
                        credentials: "include",
                    })
                        .then((response) => {
                            if (!response.ok) return decodeErrorResponse(response);
                            return response.text();
                        })
                        .then(() => {
                            let updatedUsers = users.filter((user) => user.id !== userId);
                            setUsers(updatedUsers);
                            void Swal.fire("Deleted!", "User has been deleted.", "success");
                        })
                        .catch((error) => {
                            void Swal.fire("Error!", `Failed to delete user: ${error.message}`, "error");
                        });
                }
            });
        },
        [users],
    );
    const editUser = useCallback(
        (userId: number) => {
            const user: User = users.find((user) => user.id === userId)!;
            setUserToEdit(user);
            setShowAddOrEditUserModal(true);
        },
        [users],
    );

    const columnDefinitions: ColDef[] = [
        {
            field: "givenName",
            headerName: "First Name",
            cellClass: "px-2 py-3",
            width: 200,
            cellDataType: "text",
            filter: "agTextColumnFilter",
        },
        {
            field: "lastName",
            headerName: "Last Name",
            cellClass: "px-2 py-3",
            width: 200,
            cellDataType: "text",
            filter: "agTextColumnFilter",
        },
        {
            field: "email",
            headerName: "Email",
            width: 250,
            cellClass: "px-2 py-3",
            cellDataType: "text",
            filter: "agTextColumnFilter",
        },
        {
            field: "isAdmin",
            headerName: "Admin",
            cellClass: "px-2 py-3",
            cellRenderer: (params: { value: boolean }) => (
                <Badge
                    variant={params.value ? "default" : "secondary"}
                    className={cn("px-2 py-1", params.value ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-700")}
                >
                    {params.value ? "Yes" : "No"}
                </Badge>
            ),
            // Same Yes/No the badge above renders — the Set Filter would
            // otherwise offer the raw True/False behind it.
            ...booleanSetFilter("Yes", "No"),
        },
        {
            field: "lastVisited",
            headerName: "Last Visited",
            valueFormatter: dateFormatter,
            cellClass: "px-2 py-3",
            width: 250,
            filter: "agDateColumnFilter",
            filterParams: utcDateFilterParams,
            cellDataType: "date",
        },
        {
            field: "visibleOwners",
            headerName: "Visible Owners",
            cellRenderer: VisibleOwnersRenderer,
            width: 400,
            cellClass: "overflow-hidden", // Add this to ensure content doesn't overflow
            autoHeight: false, // Remove this or set to false
            filter: "agTextColumnFilter",
            tooltipComponentParams: { color: "#55AA77" },
            tooltipValueGetter: (params: CustomTooltipProps) => visibleOwnersTooltipComponent(params, ownerIdMap),
        },
        {
            field: "actions",
            headerName: "Actions",
            cellRenderer: ActionsRenderer,
            cellRendererParams: {
                onEdit: editUser,
                onDelete: deleteUser,
            },
            cellClass: "flex items-center",
            flex: 1,
            filter: false,
        },
    ];

    useEffect(() => {
        fetch(`${apiUrl}/api/admin/users`, fetchOptions)
            .then((response) => response.text())
            .then((data) => {
                let users = Convert.toGetAllUsersResponse(data).users;
                setUsers(users);
            })
            .catch((error) =>
                toast({
                    title: "Error",
                    description: `Failed to fetch users: ${error.message}`,
                    variant: "destructive",
                }),
            );
    }, []);

    const handleModalSubmitted = useCallback(
        (firstname: string, lastname: string, email: string, isAdmin: boolean, visibleOwners: number[], existingUser: User | undefined) => {
            const url = existingUser ? `${apiUrl}/api/admin/users/${existingUser.id}` : `${apiUrl}/api/admin/users`;
            const method = existingUser ? "PUT" : "POST";
            fetch(url, {
                method: method,
                headers: {
                    "Content-Type": "application/json",
                },
                credentials: "include",
                body: JSON.stringify({
                    givenName: firstname,
                    lastName: lastname,
                    email: email,
                    isAdmin: isAdmin,
                    visibleOwners: visibleOwners,
                }),
            })
                .then((response) => {
                    if (!response.ok) return decodeErrorResponse(response);
                    return response.text();
                })
                .then((data) => {
                    let updatedOrNewUser = Convert.toGetUserResponse(data).user;
                    if (existingUser) {
                        let updatedUsers = users.map((user) => (user.id === updatedOrNewUser.id ? updatedOrNewUser : user));
                        setUsers(updatedUsers);
                        successPopup("User updated successfully");
                        return;
                    } else {
                        let updatedUsers = [...users, updatedOrNewUser];
                        setUsers(updatedUsers);
                        successPopup("User added successfully");
                    }
                    setShowAddOrEditUserModal(false);
                })
                .catch((error) => {
                    void errorPopup("Failed to add user: " + error.message);
                });
        },
        [users],
    );

    const handleModalClosed = useCallback(() => {
        setUserToEdit(undefined);
        setShowAddOrEditUserModal(false);
    }, []);

    return (
        <TooltipProvider>
            <AddUserDialog open={showAddUserModal} onClose={handleModalClosed} onSubmit={handleModalSubmitted} existingUser={userToEdit} />
            <Grid<User>
                columnDefs={columnDefinitions}
                gridName={gridName}
                initialRowData={users}
                buttons={[
                    {
                        label: "Add User",
                        onClick: () => setShowAddOrEditUserModal(true),
                        icon: PlusIcon,
                    },
                ]}
                canExport={false}
                getRowId={(params) => params.data.id.toString()}
            />
        </TooltipProvider>
    );
};

export default UsersPage;
